// DesignLine Component - Feature #282
// =====================================
// Lyra's visual signature: ultra-thin two-colored horizontal stripe
// Uses mat blue + muted orange from design tokens

import React from 'react';

interface DesignLineProps {
  /** Variant determines placement and styling */
  variant?: 'horizontal' | 'top-accent';
  /** Custom width - defaults to full width */
  width?: string;
  /** Custom className for positioning */
  className?: string;
  /** Height in pixels (1-2px recommended) */
  height?: 1 | 2;
}

/**
 * DesignLine - Lyra's visual signature element
 *
 * An ultra-thin (1-2px) two-colored horizontal stripe using
 * mat blue (#4A6FA5) and muted orange (#D4845F).
 *
 * Variants:
 * - 'horizontal': Standard divider, equal split
 * - 'top-accent': Frame decoration with asymmetric split (70/30)
 */
const DesignLine: React.FC<DesignLineProps> = ({
  variant = 'horizontal',
  width = '100%',
  className = '',
  height = 1,
}) => {
  const heightClass = height === 1 ? 'h-px' : 'h-0.5';

  // Variant-specific styling
  const variantStyles = {
    horizontal: {
      // Standard divider: 50/50 split
      background: 'linear-gradient(to right, #4A6FA5 50%, #D4845F 50%)',
    },
    'top-accent': {
      // Frame decoration: asymmetric 70/30 split for visual interest
      background: 'linear-gradient(to right, #4A6FA5 70%, #D4845F 30%)',
    },
  };

  return (
    <div
      className={`${heightClass} ${className}`}
      style={{
        width,
        background: variantStyles[variant].background,
      }}
      role="presentation"
      aria-hidden="true"
    />
  );
};

export default DesignLine;
